import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminApi } from '../../services/api';
import {
  ChevronRight, ChevronLeft, X, Building, Palette, Gift,
  UserPlus, Send, CheckCircle2, Sparkles, Rocket
} from 'lucide-react';

const STEPS = [
  {
    id: 'welcome',
    icon: Rocket,
    color: '#D44E20',
    title: 'Benvenuto su Lazo!',
    subtitle: 'Ti guidiamo nella configurazione del tuo programma fedeltà in pochi passi.',
    content: 'Lazo ti aiuta a fidelizzare i clienti del tuo locale con punti, premi, giochi e comunicazioni automatiche. Configura tutto in meno di 5 minuti.',
    action: null,
  },
  {
    id: 'locale',
    icon: Building,
    color: '#3B82F6',
    title: 'Il tuo locale',
    subtitle: 'Verifica le informazioni base del tuo locale.',
    content: 'Vai nella sezione "Locale" per aggiornare nome, indirizzo, orari e contatti. Queste info appariranno nelle comunicazioni ai clienti.',
    action: { label: 'Vai a Locale', path: '/admin/locale' },
  },
  {
    id: 'branding',
    icon: Palette,
    color: '#8B5CF6',
    title: 'Personalizza il brand',
    subtitle: 'Logo, colori e stile della tua bacheca cliente.',
    content: 'Carica il logo e scegli i colori del tuo brand. La bacheca cliente si adatterà automaticamente al tuo stile.',
    action: { label: 'Vai a Branding', path: '/admin/branding' },
  },
  {
    id: 'rewards',
    icon: Gift,
    color: '#EF4444',
    title: 'Crea il primo premio',
    subtitle: 'I premi motivano i clienti a tornare.',
    content: 'Definisci almeno un premio riscattabile con punti. Esempi: "Caffè gratis" a 50 punti, "Sconto 10%" a 100 punti.',
    action: { label: 'Vai ai Premi', path: '/admin/premi' },
  },
  {
    id: 'staff',
    icon: UserPlus,
    color: '#10B981',
    title: 'Invita lo staff',
    subtitle: 'Il tuo team potrà registrare clienti e gestire punti.',
    content: 'Aggiungi i membri dello staff con il ruolo "Cassiere" o "Supervisore". Riceveranno un PIN per accedere all\'app staff.',
    action: { label: 'Vai a Staff', path: '/admin/staff' },
  },
  {
    id: 'done',
    icon: CheckCircle2,
    color: '#10B981',
    title: 'Tutto pronto!',
    subtitle: 'Il tuo programma fedeltà è configurato.',
    content: 'Puoi esplorare tutte le funzionalità dalla sidebar. Broadcast, automazioni, giochi, coupon e molto altro ti aspettano. Buon lavoro!',
    action: null,
  },
];

export default function OnboardingWizard({ onComplete, initialStep = 0 }) {
  const [step, setStep] = useState(initialStep);
  const current = STEPS[step];
  const Icon = current.icon;
  const progress = ((step + 1) / STEPS.length) * 100;

  const handleNext = async () => {
    if (step < STEPS.length - 1) {
      const next = step + 1;
      setStep(next);
      try { await adminApi.put('/admin/onboarding/step', { step: next }); } catch {}
    } else {
      try { await adminApi.put('/admin/onboarding/complete'); } catch {}
      onComplete();
    }
  };

  const handleSkip = async () => {
    try { await adminApi.put('/admin/onboarding/complete'); } catch {}
    onComplete();
  };

  const handleAction = () => {
    if (current.action?.path) {
      window.location.href = current.action.path;
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      data-testid="onboarding-wizard">

      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} transition={{ type: 'spring', damping: 25 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">

        {/* Progress bar */}
        <div className="h-1.5 bg-slate-100">
          <motion.div animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }}
            className="h-full rounded-full bg-[#D44E20]" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">
            Passo {step + 1} di {STEPS.length}
          </span>
          <button onClick={handleSkip} data-testid="wizard-skip"
            className="text-xs text-[#94A3B8] hover:text-[#64748B] font-medium transition-colors flex items-center gap-1">
            <X size={12} /> Salta tutto
          </button>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }} className="px-6 py-6 text-center">

            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: `${current.color}12` }}>
              <Icon size={28} style={{ color: current.color }} />
            </div>

            <h2 className="text-xl font-bold text-[#1A1A1A] mb-1">{current.title}</h2>
            <p className="text-sm text-[#D44E20] font-medium mb-3">{current.subtitle}</p>
            <p className="text-sm text-[#64748B] leading-relaxed max-w-sm mx-auto">{current.content}</p>

            {current.action && (
              <button onClick={handleAction} data-testid={`wizard-action-${current.id}`}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium border border-slate-200 text-[#1A1A1A] hover:bg-slate-50 transition-colors">
                {current.action.label} <ChevronRight size={14} />
              </button>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Footer nav */}
        <div className="flex items-center justify-between px-6 pb-6">
          <button onClick={() => step > 0 && setStep(step - 1)} disabled={step === 0}
            data-testid="wizard-prev"
            className="flex items-center gap-1 px-4 py-2 rounded-xl text-sm font-medium text-[#64748B] hover:bg-slate-50 disabled:opacity-30 transition-colors">
            <ChevronLeft size={14} /> Indietro
          </button>
          <button onClick={handleNext} data-testid="wizard-next"
            className="flex items-center gap-1 px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-[#D44E20] hover:bg-[#B3421A] transition-colors">
            {step === STEPS.length - 1 ? (
              <><Sparkles size={14} /> Inizia!</>
            ) : (
              <>Avanti <ChevronRight size={14} /></>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
